import { useEffect, useState } from 'react';
import { useQuery, useInfiniteQuery, useMutation } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { useTranslation } from 'react-i18next';

// Components
import { Alert, Box, Snackbar, Stack, Tab, Tabs } from '@mui/material';
import { Container } from 'Components/Container';
import { Column, Row } from 'Components/Grid';
import { ColumnEdit, LinkEdit, RowExhibition } from './styles';
import { PageLoaderShow } from 'Components/PageLoader/Show';
import { DefaultTable } from 'Components/DefaultTable';

// Icons
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { DotsLoader } from 'Components/DotsLoader';

// Services
import {
  checkDisclaimerInstall,
  createDisclaimerScan,
  createDisclaimerScheduleInstallScan,
  getCookieById,
  getCookieConsentByTenant,
} from 'services/cookies-service';
import { tableHeaderCategory, tableHeaderConsent } from './table-header';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { ButtonForm } from 'Components/Form/styles';
import { CardIntegrationStatus } from './components/CardIntegrationStatus';
import { CardTotalVisitors } from './components/CardTotalVisitors';
import { CookiesDetail } from './components/CookiesDetails';
import { CardCookieBannerCode } from './components/CardCookieBannerCode';
import { CardConfigScan } from './components/CardConfigScan';
import { CardConfigInstall } from './components/CardConfigInstall';
import { TabPanel } from 'Components/TabPanel';

export const CookiesShow = () => {
  const { t } = useTranslation(['cookies']);
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);
  const { id } = useParams();
  const SwalReact = withReactContent(Swal);
  const [tabPosition, setTabPosition] = useState(0);
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  const {
    isLoading,
    error,
    data: cookie,
  } = useQuery('getCookieById', async () => {
    const dataCookie = await getCookieById(id);
    return dataCookie.status === 200 ? dataCookie.data : null;
  });

  const {
    data: cookieConsent,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['getCookieConsentByTenant'],
    queryFn: async ({ pageParam }) => {
      if (selectedTenant.slug === undefined) {
        return {
          consents: [],
          lastId: null,
        };
      }
      const dataCookieConsent = await getCookieConsentByTenant(selectedTenant.slug, pageParam);
      if (dataCookieConsent.status === 200) {
        return {
          consents: dataCookieConsent.data.consents,
          lastIds: dataCookieConsent.data.lastIds,
        };
      }
      return {
        consents: [],
        lastIds: null,
      };
    },
    getNextPageParam: lastPage => lastPage.lastIds || null,
  });

  const onClickEdit = () => {
    navigate('/cookies/edit/' + id);
  };

  const getPositionBanner = position => {
    switch (position) {
      case 'left':
        return t('cookies:show.positions.bottomLeft');
      default:
        return t('cookies:show.positions.bottomRight');
    }
  };

  const showDisclaimerDetailModal = item => {
    const contentHtml = <CookiesDetail name={item.name} cookies={item.cookies} />;

    SwalReact.fire({
      // title: 'Detalhe do consentimento!',
      html: contentHtml,
      // icon: 'info',
    });
  };

  const mutationMonthlySchedule = useMutation(
    async disclaimerId => {
      // envia id do disclaimer para validação no backend
      if (disclaimerId == null) throw new Error('Aviso não identificado verifique se o identificador esta correto!');
      return createDisclaimerScan(disclaimerId);
    },
    {
      onError: error => {
        const errorMessage = error.response.data.message || error.message;
        SwalReact.fire({
          title: t('cookies:show.swal.errorTitle'),
          text: errorMessage,
          icon: 'error',
        });
      },
      onSuccess: () => {},
    },
  );

  const mutationScheduleInstallScan = useMutation(
    async disclaimerId => {
      // envia id do disclaimer para validação no backend
      if (disclaimerId == null) throw new Error('Aviso não identificado verifique se o identificador esta correto!');
      return createDisclaimerScheduleInstallScan(disclaimerId);
    },
    {
      onError: error => {
        const errorMessage = error.response.data.message || error.message;
        SwalReact.fire({
          title: t('cookies:show.swal.errorTitle'),
          text: errorMessage,
          icon: 'error',
        });
      },
      onSuccess: () => {},
    },
  );

  const mutationCheckInstall = useMutation(
    disclaimerId => {
      // envia id do disclaimer para validação no backend
      if (disclaimerId == null) throw new Error('Aviso não identificado verifique se o identificador esta correto!');
      return checkDisclaimerInstall(disclaimerId);
    },
    {
      onError: error => {
        const errorMessage = error.response.data.message || error.message;
        SwalReact.fire({
          title: t('cookies:show.swal.errorTitle'),
          text: errorMessage,
          icon: 'error',
        });
      },
      onSuccess: () => {
        SwalReact.fire({
          title: t('cookies:show.swal.successTitle'),
          text: t('cookies:show.swal.installVerified'),
          icon: 'success',
        });
      },
    },
  );

  useEffect(() => {
    if (mutationMonthlySchedule.isSuccess) {
      SwalReact.fire({
        title: t('cookies:show.swal.successTitle'),
        text: t('cookies:show.swal.scanScheduled'),
        icon: 'success',
      });
    }
  }, [SwalReact, mutationMonthlySchedule.isSuccess]);

  return (
    <>
      {!isLoading && !error && (
        <Container title={`${cookie?.domain}`} breadcrumb={`Adequações / ${cookie?.domain}`} linkPage="/cookies">
          <Box sx={{ width: '100%' }} className="container">
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabPosition}
                onChange={(_, newValue) => setTabPosition(newValue)}
                aria-label="basic tabs example"
              >
                <Tab
                  label={t('cookies:show.tabs.dashboard')}
                  id="tab-dashboard"
                  aria-controls="tabpanel-integration"
                  style={{ marginLeft: 0, marginTop: 0, width: 'fit-content' }}
                />
                <Tab
                  label={t('cookies:show.tabs.positioning')}
                  id="tab-details"
                  aria-controls="tabpanel-details"
                  style={{ marginLeft: 0, marginTop: 0, width: 'fit-content' }}
                />
                <Tab
                  label={t('cookies:show.tabs.categorization')}
                  id="tab-category"
                  aria-controls="tabpanel-category"
                  style={{ marginLeft: 0, marginTop: 0, width: 'fit-content' }}
                />
                <Tab
                  label={t('cookies:show.tabs.consent')}
                  id="tab-consent"
                  aria-controls="tabpanel-consent"
                  style={{ marginLeft: 0, marginTop: 0, width: 'fit-content' }}
                />
              </Tabs>
            </Box>
            <TabPanel value={tabPosition} index={0} id="tabpanel-dashboard" ariaLabel="tab-dashboard">
              <Row>
                <Column xs={12} md={6}>
                  <CardIntegrationStatus disclaimerStatusAtivo={cookie.disclaimerStatusAtivo} />
                </Column>
                <Column xs={12} md={6}>
                  <CardTotalVisitors totalVisitors={cookie.totalVisitors} />
                </Column>
              </Row>
              <Row>
                <Column xs={12}>
                  <CardCookieBannerCode id={id} setShowCopySuccess={setShowCopySuccess} />
                </Column>
              </Row>
              {cookie.disclaimerStatusAtivo && (
                <CardConfigScan
                  id={id}
                  disclaimerMonthlySchedule={cookie.disclaimerMonthlySchedule}
                  mutationMonthlySchedule={mutationMonthlySchedule}
                />
              )}
              {!cookie.disclaimerStatusAtivo && (
                <CardConfigInstall
                  id={id}
                  mutationScheduleInstallScan={mutationScheduleInstallScan}
                  mutationCheckInstall={mutationCheckInstall}
                />
              )}
            </TabPanel>
            <TabPanel value={tabPosition} index={1} id="tabpanel-details" ariaLabel="tab-details">
              <RowExhibition>
                <Column xs={3}>{t('cookies:show.fields.site')}</Column>
                <Column>{cookie.domain}</Column>
                <ColumnEdit xs={2}>
                  <LinkEdit onClick={onClickEdit}>
                    <EditOutlinedIcon style={{ fill: '#0039C5' }} />
                    {t('cookies:show.fields.editButton')}
                  </LinkEdit>
                </ColumnEdit>
              </RowExhibition>
              <RowExhibition>
                <Column xs={3}>{t('cookies:show.fields.bgColor')}</Column>
                <Column flex style={{ alignItems: 'center' }}>
                  {cookie.background_color}
                  <div
                    style={{
                      backgroundColor: cookie.background_color,
                      width: '30px',
                      height: '15px',
                      display: 'inline-flex',
                      marginInlineStart: '8px',
                      border: '1px solid #ccc',
                    }}
                  >
                    &nbsp;
                  </div>
                </Column>
                <ColumnEdit xs={2}>
                  <LinkEdit onClick={onClickEdit}>
                    <EditOutlinedIcon style={{ fill: '#0039C5' }} />
                    {t('cookies:show.fields.editButton')}
                  </LinkEdit>
                </ColumnEdit>
              </RowExhibition>
              <RowExhibition>
                <Column xs={3}>{t('cookies:show.fields.primaryColor')}</Column>
                <Column flex style={{ alignItems: 'center' }}>
                  {cookie.primary_color}
                  <div
                    style={{
                      backgroundColor: cookie.primary_color,
                      width: '30px',
                      height: '15px',
                      display: 'inline-flex',
                      marginInlineStart: '8px',
                      border: '1px solid #ccc',
                    }}
                  >
                    &nbsp;
                  </div>
                </Column>
                <ColumnEdit xs={2}>
                  <LinkEdit onClick={onClickEdit}>
                    <EditOutlinedIcon style={{ fill: '#0039C5' }} />
                    {t('cookies:show.fields.editButton')}
                  </LinkEdit>
                </ColumnEdit>
              </RowExhibition>
              <RowExhibition>
                <Column xs={3}>{t('cookies:show.fields.textColor')}</Column>
                <Column flex style={{ alignItems: 'center' }}>
                  {cookie.text_color}
                  <div
                    style={{
                      backgroundColor: cookie.text_color,
                      width: '30px',
                      height: '15px',
                      display: 'inline-flex',
                      marginInlineStart: '8px',
                      border: '1px solid #ccc',
                    }}
                  >
                    &nbsp;
                  </div>
                </Column>
                <ColumnEdit xs={2}>
                  <LinkEdit onClick={onClickEdit}>
                    <EditOutlinedIcon style={{ fill: '#0039C5' }} />
                    {t('cookies:show.fields.editButton')}
                  </LinkEdit>
                </ColumnEdit>
              </RowExhibition>
              <RowExhibition>
                <Column xs={3}>{t('cookies:show.fields.defaultLanguage')}</Column>
                <Column flex style={{ alignItems: 'center' }}>
                  {cookie.language}
                </Column>
                <ColumnEdit xs={2}>
                  <LinkEdit onClick={onClickEdit}>
                    <EditOutlinedIcon style={{ fill: '#0039C5' }} />
                    {t('cookies:show.fields.editButton')}
                  </LinkEdit>
                </ColumnEdit>
              </RowExhibition>
              <RowExhibition>
                <Column xs={3}>{t('cookies:show.fields.bannerPosition')}</Column>
                <Column flex style={{ alignItems: 'center' }}>
                  {getPositionBanner(cookie.position_controller)}
                </Column>
                <ColumnEdit xs={2}>
                  <LinkEdit onClick={onClickEdit}>
                    <EditOutlinedIcon style={{ fill: '#0039C5' }} />
                    {t('cookies:show.fields.editButton')}
                  </LinkEdit>
                </ColumnEdit>
              </RowExhibition>
            </TabPanel>
            <TabPanel value={tabPosition} index={2} id="tabpanel-category" ariaLabel="tab-category" boxPadding={1}>
              <DefaultTable
                rows={tableHeaderCategory(t)}
                cells={cookie.categories || []}
                onClickModalDetail={item => {
                  showDisclaimerDetailModal(item);
                }}
                hideDelete
                hideEdit
                hideShow
                showModalDetail
                showNoDataLine
                link=""
              />
            </TabPanel>
            <TabPanel value={tabPosition} index={3} id="tabpanel-consent" ariaLabel="tab-consent" boxPadding={1}>
              <DefaultTable
                rows={tableHeaderConsent(t)}
                cells={cookieConsent?.pages.flatMap(page => page.consents) ?? []}
                hideDelete
                hideShow
                hideEdit
                link=""
              />
              <Stack sx={{ justifyContent: 'center', flexDirection: 'row', marginTop: '16px' }}>
                <ButtonForm
                  label={isFetchingNextPage ? <DotsLoader /> : t('cookies:show.loadMore')}
                  onClick={() => fetchNextPage()}
                  variant="link"
                  disabled={!hasNextPage || isFetchingNextPage}
                />
              </Stack>
            </TabPanel>
          </Box>
          <Snackbar
            open={showCopySuccess}
            onClose={() => {
              setShowCopySuccess(false);
            }}
          >
            <Alert
              onClose={() => {
                setShowCopySuccess(false);
              }}
              severity="success"
              sx={{ width: '100%' }}
            >
              {t('cookies:show.copied')}
            </Alert>
          </Snackbar>
        </Container>
      )}
      {isLoading && <PageLoaderShow />}
      {error && <div>Erro</div>}
    </>
  );
};
